/** @file Collects name labels: bones at their midpoint, visible slots at their attachment, sockets at a. */
import type { LabelRequest } from '../labels/placeLabels';
import type { Sink } from '../draw/createSink';
import type { OverlayInput } from '../overlayInput';
import { OVERLAY_STYLE } from '../overlayStyle';
import { boneSegment } from './boneSegment';
import { refillSet } from './refillSet';
import { slotAnchor } from './slotAnchor';

const seg = new Float64Array(4);
const at: [number, number] = [0, 0];
/** Selected bones or slots, refilled per call. */
const selected = new Set<number>();

/** Bone labels (kind 'bone'), anchored halfway along the bone; selected bones in yellow. */
function boneNames(input: OverlayInput, labels: LabelRequest[]): void {
  refillSet(selected, input.options.selection?.bones);
  const bones = input.data.bones;
  for (let i = 0; i < bones.length; i++) {
    const bone = bones[i];
    if (bone?.kind !== 'bone') continue;
    boneSegment(input.pose, input.camera, i, bone.length, seg);
    const x = ((seg[0] ?? NaN) + (seg[2] ?? NaN)) / 2, y = ((seg[1] ?? NaN) + (seg[3] ?? NaN)) / 2;
    labels.push({ text: bone.name, x, y, color: selected.has(i) ? OVERLAY_STYLE.selected : OVERLAY_STYLE.labelText });
  }
}

/** Slot labels for slots that show an attachment, in draw order; selected slots in yellow. */
function slotNames(input: OverlayInput, labels: LabelRequest[]): void {
  refillSet(selected, input.options.selection?.slots);
  const order = input.pose.drawOrder;
  for (let k = 0; k < order.length; k++) {
    const slot = order[k] ?? -1;
    if ((input.pose.slotAttachment[slot] ?? -1) < 0) continue;
    slotAnchor(input, slot, at);
    labels.push({ text: input.data.slots[slot]?.name ?? '', x: at[0], y: at[1], color: selected.has(slot) ? OVERLAY_STYLE.selected : OVERLAY_STYLE.labelText });
  }
}

/** Socket labels at the socket origin. */
function socketNames(input: OverlayInput, labels: LabelRequest[]): void {
  const sockets = input.data.sockets;
  for (let k = 0; k < sockets.length; k++) {
    const socket = sockets[k];
    if (socket === undefined) continue;
    boneSegment(input.pose, input.camera, socket.bone, 0, seg);
    labels.push({ text: socket.name, x: seg[0] ?? NaN, y: seg[1] ?? NaN, color: OVERLAY_STYLE.socket });
  }
}

/** `options.boneNames`, `options.slotNames`, and socket names with `options.sockets` (draws nothing itself). */
export function collectNames(sink: Sink, input: OverlayInput, labels: LabelRequest[]): void {
  if (input.options.boneNames === true) boneNames(input, labels);
  if (input.options.slotNames === true) slotNames(input, labels);
  if (input.options.sockets === true) socketNames(input, labels);
}
